import { useRef } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useParams, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { aiqApi } from '@/api/aiq'
import type { AiqDiagnosticReportResponse, AiqDomainScore } from '@/api/aiq'
import type { AiqDomain } from '@/constants/aiq'
import { DOMAIN_ORDER, bandOf } from '@/constants/aiq'
import { parseDiagnosticReportVersion } from '@/utils/diagnosticReportVersion'
import {
  AIQ_BAND_COLORS,
  AIQ_REPORT_PAGE_STYLE,
  AiqBandIndicator,
  AiqReportPrintActions,
  AiqReportHeader,
  DomainComparisonChart,
  MetaPill,
  MiniBellCurve,
  PageNumber,
  StudentAiqReportContent,
} from './StudentAiqReport'

const ABILITY_ASSET_BASE = '/report/diagnostic/ability'

/** 능력당 예시 1개 고정. 풀이 그림은 Figma 원본 프레임 그대로(305px 폭, 3배 해상도 PNG) 사용한다. */
const ABILITY_EXAMPLE: Record<AiqDomain, { slug: string; iconSize: [number, number] | null; imageHeight: number }> = {
  abstraction: { slug: 'ignore', iconSize: null, imageHeight: 322 },
  change: { slug: 'change', iconSize: [21.17, 19.25], imageHeight: 350 },
  relation: { slug: 'relation', iconSize: [20.83, 19.45], imageHeight: 300 },
}

const COHORT_PLOT_HEIGHT = 111
const COHORT_BAR_MAX_HEIGHT = 88

export const DIAGNOSTIC_AIQ_PAGE_COUNT = 1 + DOMAIN_ORDER.length

/** 수학머리 단독 리포트 화면 (`/admin/students/:userId/aiq-report`). */
export default function DiagnosticAiqReportPage() {
  const { t } = useTranslation('admin')
  const { userId } = useParams<{ userId: string }>()
  const numericId = userId ? Number(userId) : null
  // `?version=v1`은 개편 전 2쪽, `v2`는 능력별 4쪽. 값이 없거나 잘못되면 기본 리포트.
  const [searchParams] = useSearchParams()
  const version = parseDiagnosticReportVersion(searchParams.get('version'))

  const { data, isLoading } = useQuery({
    queryKey: ['aiqDiagnosticReport', numericId],
    queryFn: async () => {
      if (!numericId) return null
      const res = await aiqApi.getDiagnosticReport(numericId)
      return res.data.data
    },
    enabled: !!numericId,
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }
  if (!data) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white p-16 text-center text-gray-500 shadow-sm">
        {t('studentAiqReport.empty')}
      </div>
    )
  }

  return version === 'v1' ? <StudentAiqReportContent data={data} /> : <DiagnosticAiqReportContent data={data} />
}

/** 진단 리포트의 수학머리 4쪽: 결과 1쪽 + 능력별(무시·변화·관계) 3쪽. */
export function DiagnosticAiqReportContent({
  data,
  showPrintButton = true,
  className = 'mx-auto max-w-[794px] bg-white shadow-lg print:shadow-none',
  itemCount,
  elapsedMin,
  pageStart = 3,
  gradeLabel,
}: {
  data: AiqDiagnosticReportResponse
  showPrintButton?: boolean
  className?: string
  itemCount?: number | null
  elapsedMin?: number | null
  pageStart?: number
  gradeLabel?: string
}) {
  const printRef = useRef<HTMLDivElement>(null)
  const abilityScores = DOMAIN_ORDER
    .map((domain) => data.domainScores.find((score) => score.domain === domain))
    .filter((score): score is AiqDomainScore => score !== undefined)

  return (
    <div
      ref={printRef}
      className={className}
      id="aiq-report-root"
      style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
    >
      {showPrintButton && <AiqReportPrintActions contentRef={printRef} />}
      <AiqResultPage
        data={data}
        gradeLabel={gradeLabel}
        itemCount={itemCount}
        elapsedMin={elapsedMin}
        pageNumber={pageStart}
      />
      {abilityScores.map((score, index) => (
        <AiqAbilityPage
          key={score.domain}
          data={data}
          score={score}
          gradeLabel={gradeLabel}
          pageNumber={pageStart + 1 + index}
        />
      ))}
    </div>
  )
}

/** dense: 유형 문구가 긴 능력 쪽이 한 장에 들어가도록 구역 간격을 28px에서 24px로 줄인다. */
function ReportPage({ testId, dense = false, children }: { testId: string; dense?: boolean; children: ReactNode }) {
  return (
    <section
      data-testid={testId}
      className={`diagnostic-report-page relative flex break-before-page flex-col bg-white px-12 pb-12 pt-10 ${dense ? 'gap-6' : 'gap-7'}`}
      style={AIQ_REPORT_PAGE_STYLE}
    >
      {children}
    </section>
  )
}

function AiqResultPage({
  data,
  gradeLabel,
  itemCount,
  elapsedMin,
  pageNumber,
}: {
  data: AiqDiagnosticReportResponse
  gradeLabel?: string
  itemCount?: number | null
  elapsedMin?: number | null
  pageNumber: number
}) {
  const { t } = useTranslation('admin')
  const band = bandOf(data.aiqScore)
  const activeColor = AIQ_BAND_COLORS[band]
  // 응답한 총 문항 수: 호출부가 넘기지 않으면 능력별 문항 수 합으로 대신한다.
  const answeredCount = itemCount ?? (data.domainScores.reduce((sum, score) => sum + (score.itemCount ?? 0), 0) || null)

  return (
    <ReportPage testId="aiq-report-result-page">
      <AiqReportHeader data={data} gradeLabel={gradeLabel} />

      <section className="flex w-full flex-col gap-8">
        <div className="flex items-center justify-between pr-14">
          <div className="flex flex-col gap-6 px-2">
            <div
              data-testid="aiq-summary-headline"
              className="font-bold leading-[1.7] tracking-[-0.6px] text-[#131313]"
            >
              <p className="text-lg">
                {t('diagnosticAiqReport.summary.indexPrefix', { nickName: data.nickName })}
                <span data-testid="aiq-summary-score" style={{ color: activeColor }}>{data.aiqScore}</span>
                {t('diagnosticAiqReport.summary.indexSuffix')}
              </p>
              <p className="text-xl">
                {t('diagnosticAiqReport.summary.rankPrefix')}
                <span data-testid="aiq-summary-rank" style={{ color: activeColor }}>{data.aiqRank}%</span>
                {t('diagnosticAiqReport.summary.levelPrefix')}
                <span data-testid="aiq-summary-level" style={{ color: activeColor }}>
                  {t(`studentAiqReport.summary.level.${band}`)}
                </span>
                {t('diagnosticAiqReport.summary.suffix')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <MetaPill>
                {answeredCount == null
                  ? t('studentAiqReport.summary.itemCountEmpty')
                  : t('studentAiqReport.summary.itemCount', { count: answeredCount })}
              </MetaPill>
              <MetaPill>
                <span className="text-xs font-semibold text-[#888]">{t('studentAiqReport.summary.elapsed')}</span>
                <span>{t('studentAiqReport.summary.elapsedMin', { value: elapsedMin ?? '—' })}</span>
              </MetaPill>
            </div>
          </div>
          <AiqBandIndicator nickName={data.nickName} band={band} activeColor={activeColor} />
        </div>

        <div data-testid="aiq-intro" className="flex flex-col gap-2 px-2 tracking-[-0.6px] text-[#131313]">
          <p className="text-base font-bold leading-[1.7]">{t('diagnosticAiqReport.intro.headline')}</p>
          <p className="whitespace-pre-line break-keep text-sm leading-[1.5]">{t('diagnosticAiqReport.intro.body')}</p>
        </div>

        <div className="flex flex-col gap-4">
          <section data-testid="aiq-distribution-card" className="flex items-start gap-6 rounded-[10px] bg-[#f5f5f5] px-6 py-5">
            <CardText
              title={t('studentAiqReport.distTitle')}
              desc={t('diagnosticAiqReport.dist.desc')}
              note={t('diagnosticAiqReport.dist.caption')}
            />
            <div className="min-w-0 flex-1">
              <MiniBellCurve score={data.aiqScore} rank={data.aiqRank} />
            </div>
          </section>

          <section data-testid="aiq-comparison-card" className="flex items-start gap-6 rounded-[10px] bg-[#f5f5f5] px-6 py-5">
            <CardText
              title={t('studentAiqReport.compareTitle')}
              desc={t('diagnosticAiqReport.compare.desc')}
              note={t('diagnosticAiqReport.compare.footnote')}
            />
            <CohortBars
              items={[
                { key: 'self', label: data.nickName, value: data.aiqScore, self: true },
                { key: 'all', label: `${t('diagnosticAiqReport.group.all')}*`, value: data.sameAgeAiqScore },
                { key: 'top', label: `${t('diagnosticAiqReport.group.top')}*`, value: data.quebonAiqScore },
              ]}
            />
          </section>

          <section data-testid="aiq-detail-card" className="flex flex-col gap-4 rounded-[10px] bg-[#f5f5f5] px-6 py-5">
            <div className="flex flex-col gap-2 leading-[1.5] tracking-[-0.6px] text-[#131313]">
              <h2 className="text-sm font-bold">{t('diagnosticAiqReport.detail.title')}</h2>
              <p className="text-xs">{t('diagnosticAiqReport.detail.desc')}</p>
            </div>
            <DomainComparisonChart
              compact
              domainScores={data.domainScores}
              nickName={data.nickName}
              copy={{
                sameGroupLabel: t('diagnosticAiqReport.group.all'),
                topGroupLabel: t('diagnosticAiqReport.group.top'),
              }}
            />
          </section>
        </div>
      </section>

      <PageNumber value={pageNumber} />
    </ReportPage>
  )
}

function CardText({ title, desc, note }: { title: string; desc: string; note: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 leading-[1.5]">
      <h2 className="text-sm font-bold tracking-[-0.6px] text-[#131313]">{title}</h2>
      <p className="text-xs tracking-[-0.6px] text-[#131313]">{desc}</p>
      <p className="whitespace-pre-line text-xs font-medium text-[#888]">{note}</p>
    </div>
  )
}

function CohortBars({ items }: { items: { key: string; label: string; value: number; self?: boolean }[] }) {
  return (
    <div data-testid="aiq-cohort-chart" className="w-[313px] shrink-0 break-inside-avoid">
      <div data-testid="aiq-cohort-plot" className="relative border-b border-[#d0d0d0]" style={{ height: COHORT_PLOT_HEIGHT }}>
        <div className="pointer-events-none absolute inset-0">
          {[0, 25, 50, 75].map((top) => (
            <div key={top} className="absolute inset-x-0 border-t border-dashed border-[#d0d0d0]" style={{ top: `${top}%` }} />
          ))}
        </div>
        <div className="absolute inset-0 grid grid-cols-3 items-end px-7">
          {items.map((item) => (
            <div key={item.key} className="flex h-full min-w-0 flex-col items-center justify-end">
              <span
                data-testid={`aiq-cohort-value-${item.key}`}
                className={`mb-1.5 text-xs leading-[1.2] ${item.self ? 'font-bold text-[#4599ff]' : 'font-semibold text-[#888]'}`}
              >
                {item.value}
              </span>
              <div
                data-testid={`aiq-cohort-bar-${item.key}`}
                className={`w-6 rounded-t-[2px] ${item.self ? 'bg-[#4599ff]' : 'bg-[#d0d0d0]'}`}
                style={{ height: (Math.max(0, Math.min(150, item.value)) / 150) * COHORT_BAR_MAX_HEIGHT }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 px-7 pt-1.5">
        {items.map((item) => (
          <span
            key={item.key}
            data-testid={`aiq-cohort-label-${item.key}`}
            className={`min-w-0 text-center text-[10px] font-medium leading-[1.5] ${item.self ? 'text-[#4599ff]' : 'text-[#888]'}`}
          >
            {item.label}
          </span>
        ))}
      </div>
    </div>
  )
}

function AiqAbilityPage({
  data,
  score,
  gradeLabel,
  pageNumber,
}: {
  data: AiqDiagnosticReportResponse
  score: AiqDomainScore
  gradeLabel?: string
  pageNumber: number
}) {
  const { t } = useTranslation('admin')
  const domain = score.domain
  const abilityName = t(`diagnosticAiqReport.ability.${domain}.name`)

  return (
    <ReportPage testId={`aiq-report-ability-page-${domain}`} dense>
      <AiqReportHeader
        data={data}
        gradeLabel={gradeLabel}
        subtitle={t('diagnosticAiqReport.headerSubtitle', { ability: abilityName })}
      />

      <section className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <AbilityIcon domain={domain} />
          <h2 className="text-xl font-extrabold leading-[1.2] tracking-[-0.6px] text-[#4599ff]">{abilityName}</h2>
        </div>
        <div className="flex flex-col gap-2 rounded-[10px] bg-[#edf5ff] px-5 py-4 tracking-[-0.6px]">
          <p className="text-sm font-bold leading-[1.5] text-[#131313]">{t(`diagnosticAiqReport.ability.${domain}.headline`)}</p>
          <p className="break-keep text-xs leading-[1.5] text-[#333]">
            {t(`diagnosticAiqReport.ability.${domain}.body`)}{' '}
            <span className="font-semibold">{t(`diagnosticAiqReport.ability.${domain}.bodyEmphasis`)}</span>
          </p>
        </div>
      </section>

      <AbilityScoreSection score={score} abilityName={abilityName} />
      <AbilityExample domain={domain} />

      <PageNumber value={pageNumber} />
    </ReportPage>
  )
}

function AbilityIcon({ domain }: { domain: AiqDomain }) {
  const { slug, iconSize } = ABILITY_EXAMPLE[domain]
  const src = `${ABILITY_ASSET_BASE}/icon-ability-${slug}.svg`
  // 무시 아이콘은 배경까지 포함한 32px SVG, 변화·관계는 배경 위에 얹는 아이콘만 있다.
  if (!iconSize) return <img src={src} alt="" aria-hidden="true" className="size-8 shrink-0" />
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#edf5ff]">
      <img src={src} alt="" aria-hidden="true" style={{ width: iconSize[0], height: iconSize[1] }} />
    </span>
  )
}

function AbilityScoreSection({ score, abilityName }: { score: AiqDomainScore; abilityName: string }) {
  const { t, i18n } = useTranslation('admin')
  const domain = score.domain
  const diff = score.score == null ? null : score.score - score.sameAgeScore
  const typeKey = score.abilityType ? `diagnosticAiqReport.abilityType.${domain}.${score.abilityType}` : null
  // 유형 문구가 등록된 능력·유형은 유형 이름·요약·본문을 보여주고, 없으면(유형 판정 불가 포함) 기존 점수 구간 서사를 보여준다.
  const hasTypeCopy = typeKey != null && i18n.exists(`${typeKey}.name`, { ns: 'admin' })

  return (
    <section data-testid={`aiq-ability-score-${domain}`} className="flex flex-col gap-4">
      <div className="flex items-start gap-6 pl-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p className="text-lg font-bold leading-[1.5] tracking-[-0.6px] text-[#131313]">
            {t('diagnosticAiqReport.ability.scorePrefix')}
            <span className="text-[#4599ff]">{abilityName}</span>
            {t('diagnosticAiqReport.ability.scoreMiddle')}
            <span data-testid={`aiq-ability-score-value-${domain}`} className="text-[#4599ff]">
              {score.score == null ? '—' : t('diagnosticAiqReport.ability.scoreValue', { score: score.score })}
            </span>
            {t('diagnosticAiqReport.ability.scoreSuffix')}
          </p>
          {diff != null && (
            <p data-testid={`aiq-ability-compare-${domain}`} className="text-sm font-medium text-[#888]">
              {t('diagnosticAiqReport.ability.comparePrefix')}{' '}
              <span className="font-bold tracking-[-0.6px] text-[#131313]">
                {diff === 0
                  ? t('diagnosticAiqReport.ability.same')
                  : t(diff > 0 ? 'diagnosticAiqReport.ability.higher' : 'diagnosticAiqReport.ability.lower', { diff: Math.abs(diff) })}
              </span>
            </p>
          )}
        </div>
        <div
          data-testid={`aiq-ability-correct-${domain}`}
          className="flex shrink-0 items-center gap-2 rounded-full bg-[#eee] px-2.5 py-1 leading-[21px]"
        >
          <span className="text-xs text-[#333]">{t('diagnosticAiqReport.ability.correct')}</span>
          <span className="flex items-center gap-[5px] text-sm">
            <span className="font-semibold text-[#131313]">{score.correctCount ?? '—'}</span>
            <span className="text-[#888]">/</span>
            <span className="text-[#333]">{score.itemCount ?? '—'}</span>
          </span>
        </div>
      </div>

      <div data-testid={`aiq-ability-type-${domain}`} className="flex items-start gap-3 rounded-[10px] bg-[#f5f5f5] px-5 py-4">
        {hasTypeCopy && typeKey ? (
          <>
            <div className="flex w-[206px] shrink-0 flex-col gap-2 font-bold">
              <p className="text-xs leading-normal text-[#4599ff]">{t(`${typeKey}.name`)}</p>
              {/* 긴 줄은 글자 중간이 아니라 단어 단위로 넘긴다 */}
              <p className="whitespace-pre-line break-keep text-sm leading-[1.5] tracking-[-0.6px] text-[#131313]">{t(`${typeKey}.summary`)}</p>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1 break-keep text-xs leading-[1.5] tracking-[-0.6px]">
              {/* 문단 안 줄바꿈은 기획 문구에 적힌 위치를 그대로 따른다 */}
              <p className="whitespace-pre-line font-semibold text-[#131313]">{t(`${typeKey}.lead`)}</p>
              {i18n.exists(`${typeKey}.body`, { ns: 'admin' }) && <p className="whitespace-pre-line text-[#333]">{t(`${typeKey}.body`)}</p>}
            </div>
          </>
        ) : (
          <p className="min-w-0 flex-1 break-keep text-xs leading-[1.5] tracking-[-0.6px] text-[#333]">{score.content}</p>
        )}
      </div>
    </section>
  )
}

function AbilityExample({ domain }: { domain: AiqDomain }) {
  const { t } = useTranslation('admin')
  const { slug, imageHeight } = ABILITY_EXAMPLE[domain]

  return (
    <section data-testid={`aiq-ability-example-${domain}`} className="flex flex-col gap-5">
      <div className="flex flex-col items-start gap-2 px-2">
        <span className="rounded-full bg-[#4599ff] px-2.5 py-1.5 text-[11px] font-semibold leading-normal text-white">
          {t(`diagnosticAiqReport.ability.${domain}.exampleTag`)}
        </span>
        <AbilityProblem domain={domain} />
      </div>
      <div className="flex items-start gap-2">
        {(['general', 'aiq'] as const).map((side) => (
          <div
            key={side}
            className={`flex w-[345px] shrink-0 flex-col rounded-[10px] px-5 py-4 ${side === 'general' ? 'self-stretch justify-between bg-[#fff5f5]' : 'gap-4 bg-[#edf5ff]'}`}
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1.5">
                <img
                  src={`${ABILITY_ASSET_BASE}/${side === 'general' ? 'icon-alert' : 'icon-check'}.svg`}
                  alt=""
                  aria-hidden="true"
                  className="size-3.5 shrink-0"
                />
                <p className={`text-sm font-bold leading-normal ${side === 'general' ? 'tracking-[-0.6px] text-[#ff675e]' : 'text-[#2f7fe0]'}`}>
                  {t(`diagnosticAiqReport.ability.${domain}.${side}.title`)}
                </p>
              </div>
              {/* Figma와 같이 3줄 안에 들어가도록 글자 단위로 줄바꿈한다(단어 단위면 4줄이 되어 쪽을 넘친다). */}
              <p className="text-xs leading-[1.5] tracking-[-0.6px] text-[#131313]">
                {t(`diagnosticAiqReport.ability.${domain}.${side}.desc`)}
              </p>
            </div>
            <img
              data-testid={`aiq-ability-image-${domain}-${side}`}
              src={`${ABILITY_ASSET_BASE}/img-${side}-${slug}.png`}
              alt={t(`diagnosticAiqReport.ability.${domain}.${side}.title`)}
              width={305}
              height={imageHeight}
              className="block max-w-none"
              style={{ width: 305, height: imageHeight }}
            />
          </div>
        ))}
      </div>
    </section>
  )
}

function AbilityProblem({ domain }: { domain: AiqDomain }) {
  const { t } = useTranslation('admin')
  if (domain === 'abstraction') {
    return (
      <img
        src={`${ABILITY_ASSET_BASE}/problem-ignore.png`}
        alt={t('diagnosticAiqReport.ability.abstraction.problem')}
        width={209}
        height={82}
        className="block max-w-none"
        style={{ width: 209, height: 'auto' }}
      />
    )
  }
  if (domain === 'change') {
    return (
      <div className="flex items-center gap-4 px-1">
        <Fraction numerator="201" denominator="4237" />
        <BlankBox />
        <Fraction numerator="401" denominator="4437" />
      </div>
    )
  }
  return (
    <div className="flex h-8 items-center">
      <p className="text-lg font-semibold leading-[1.2] tracking-[-0.6px] text-[#131313]">
        {t('diagnosticAiqReport.ability.relation.problem')}
      </p>
    </div>
  )
}

function BlankBox() {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md border-[1.5px] border-[#d0d0d0] bg-white text-lg font-bold leading-normal text-[#131313]">
      ?
    </span>
  )
}

function Fraction({ numerator, denominator }: { numerator: string; denominator: string }) {
  return (
    <span className="flex w-10 flex-col items-center text-base font-semibold leading-[1.2] text-[#131313]">
      <span>{numerator}</span>
      <span className="my-[5px] h-[1.5px] w-full bg-[#131313]" aria-hidden="true" />
      <span>{denominator}</span>
    </span>
  )
}
